import { Button, Dialog, Text } from '@elladx/design-system';
import { useEffect, useRef, useState } from 'react';
import { labelsApi } from '../../api/client';
import { downloadShippingLabel } from '../../utils/shipmentActions';
import type { BulkItem } from './bulkFlow';

export default function BulkLabelActions({ items }: { items: BulkItem[] }) {
  const labels = items.filter(item => item.result?.label_url);
  const [mode, setMode] = useState<'print' | 'download' | null>(null);
  const [index, setIndex] = useState(0);
  const [retry, setRetry] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [label, setLabel] = useState<{ url: string; type: string } | null>(null);
  const [problem, setProblem] = useState('');
  const [downloading, setDownloading] = useState<string | null>(null);
  const downloadRef = useRef(false);
  const downloadControllerRef = useRef<AbortController | null>(null);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const current = labels[index];
  const source = mode === 'print' ? current?.result?.label_url : null;
  useEffect(() => () => { downloadControllerRef.current?.abort(); downloadControllerRef.current = null; downloadRef.current = false; }, []);
  const close = () => {
    downloadControllerRef.current?.abort(); downloadControllerRef.current = null; downloadRef.current = false;
    setDownloading(null); setMode(null);
  };
  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | null = null;
    setLabel(null); setLoaded(false); setProblem('');
    if (source) labelsApi.fetch(source, controller.signal).then(({ blob, contentType }) => {
      if (controller.signal.aborted) return;
      objectUrl = URL.createObjectURL(blob); setLabel({ url: objectUrl, type: contentType });
    }).catch(err => { if (!controller.signal.aborted) setProblem(err instanceof Error ? err.message : 'The label could not be loaded.'); });
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [source, retry]);
  const download = async (item: BulkItem) => {
    if (!item.result?.label_url || downloadRef.current) return;
    const controller = new AbortController(); downloadControllerRef.current = controller;
    downloadRef.current = true; setDownloading(item.address.id); setProblem('');
    try {
      await downloadShippingLabel(item.result.label_url, item.result.label_file_type, item.result.shipment_id, controller.signal);
    } catch (err) { if (!controller.signal.aborted && downloadControllerRef.current === controller) setProblem(err instanceof Error ? err.message : 'The label could not be downloaded.'); }
    finally { if (downloadControllerRef.current === controller) { downloadControllerRef.current = null; downloadRef.current = false; setDownloading(null); } }
  };
  const raw = Boolean(label && ['text/plain', 'application/octet-stream'].includes(label.type));
  const supported = Boolean(label && (label.type === 'application/pdf' || label.type.startsWith('image/')));
  const print = () => {
    if (!loaded || !supported) return;
    try {
      if (label?.type === 'application/pdf') {
        const frame = frameRef.current?.contentWindow;
        if (!frame) throw new Error('The PDF print preview is unavailable. Download this label to print it.');
        frame.focus(); frame.print();
      } else window.print();
    } catch { setProblem('The browser could not open printing. Download this label and print it from your PDF or image viewer.'); }
  };
  if (!items.length) return null;
  if (!labels.length) return <Text>Your labels are saved in Orders. Label files are currently unavailable here.</Text>;
  return <>
    <Button variant="primary" fullWidth onClick={() => { setIndex(0); setMode('print'); }}>Print {labels.length} ready label{labels.length === 1 ? '' : 's'}</Button>
    <Button variant="secondary" fullWidth onClick={() => setMode('download')}>Download labels</Button>
    <Dialog open={Boolean(mode)} onClose={close} title={mode === 'print' ? `Print label ${index + 1} of ${labels.length}` : 'Download labels'} className={mode === 'print' ? 'bulk-label-print' : ''}>
      {mode === 'download' && <><Text>Download each ready label. This does not purchase or recreate labels.</Text><div className="bulk-download-list">{labels.map(item => <Button key={item.address.id} variant="secondary" onClick={() => void download(item)} disabled={Boolean(downloading)}>Download label for {item.address.name}{downloading === item.address.id && '…'}</Button>)}</div></>}
      {mode === 'print' && current && <>
        <Text className="bulk-print-copy">{current.address.name} · {current.result?.tracking_code}</Text>
        {!label && !problem && <Text role="status">Loading label securely…</Text>}
        {label?.type === 'application/pdf' && <iframe ref={frameRef} src={label.url} title={`Label for ${current.address.name}`} className="bulk-print-preview" onLoad={() => setLoaded(true)}/>}
        {label?.type.startsWith('image/') && <img src={label.url} alt={`Label for ${current.address.name}`} className="bulk-print-image" onLoad={() => setLoaded(true)} onError={() => setProblem('The label image could not be rendered. Download the label to print it.')}/>}
        {raw && <Text>This label uses thermal printer commands. Download it for a compatible label printer.</Text>}
        {label && !raw && !supported && <Text role="alert">This label format cannot be printed by the browser. Download it instead.</Text>}
        <div className="bulk-print-actions"><Button variant="primary" onClick={print} disabled={!loaded || !supported}>Print this label</Button><Button variant="secondary" onClick={() => void download(current)} disabled={Boolean(downloading)}>Download this label</Button>
          {index + 1 < labels.length ? <Button variant="secondary" onClick={() => setIndex(value => value + 1)}>Next label</Button> : <Button variant="secondary" onClick={close}>Done</Button>}
          {index > 0 && <Button variant="ghost" onClick={() => setIndex(value => value - 1)}>Previous label</Button>}
        </div>
      </>}
      {problem && <><Text role="alert" tone="error">{problem}</Text>{mode === 'print' && <Button variant="secondary" onClick={() => setRetry(value => value + 1)}>Try loading this label again</Button>}</>}
    </Dialog>
  </>;
}
